import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Comic } from "@/types/comic";
import { getCategoryName } from "@/utils/comic";

export default function HeroCarousel({ comics }: { comics: Comic[] }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (comics.length <= 1) return;
    const timer = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % comics.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [comics.length]);

  if (comics.length === 0) return null;

  const go = (dir: number) =>
    setCurrent((prev) => (prev + dir + comics.length) % comics.length);

  return (
    <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-foreground-950">
      {comics.map((comic, index) => (
        <div
          key={comic.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={comic.banner}
            alt={`${comic.title} 漫画横幅`}
            title={`${comic.title} - 蛙漫在线漫画`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-6 flex items-center">
        <div className="max-w-xl text-background-50">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/90 text-accent-950 text-xs font-label font-bold">
            <i className="ri-vip-crown-2-line"></i>
            编辑推荐 · 本周焦点
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-label font-bold leading-tight">
            {comics[current].title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs md:text-sm">
            <span className="px-2 py-0.5 rounded-md bg-white/15">
              {comics[current].authorName}
            </span>
            <span className="px-2 py-0.5 rounded-md bg-white/15">
              {comics[current].status}
            </span>
            {comics[current].categoryIds.map((id) => (
              <span
                key={id}
                className="px-2 py-0.5 rounded-md bg-primary-500/80"
              >
                {getCategoryName(id)}
              </span>
            ))}
          </div>
          <p className="mt-4 text-sm md:text-base text-background-100/90 line-clamp-3">
            {comics[current].description}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/read/${comics[current].id}/${comics[current].chapters[0]?.id ?? "1"}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold whitespace-nowrap transition-colors"
            >
              <i className="ri-book-open-line"></i>
              开始阅读
            </Link>
            <Link
              to={`/comic/${comics[current].id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-label font-bold whitespace-nowrap transition-colors"
            >
              <i className="ri-information-line"></i>
              查看详情
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一部"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-background-50 text-2xl transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      <button
        type="button"
        aria-label="下一部"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-black/30 hover:bg-black/50 text-background-50 text-2xl transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {comics.map((comic, index) => (
          <button
            key={comic.id}
            type="button"
            aria-label={`切换到 ${comic.title}`}
            onClick={() => setCurrent(index)}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              index === current ? "w-8 bg-accent-400" : "w-2 bg-white/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}